import debounce from "lodash/debounce";

export default class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};

    const self = this;
    this.state = new Proxy(
      {
        open: false,
        loading: false,
      },
      {
        set(state, key, value) {
          const oldValue = state[key];

          state[key] = value;
          if (oldValue !== value) {
            switch (key) {
              case "loading":
                self.processLoadingChange();
                break;
              case "open":
              default:
                self.processOpenChange();
            }
          }
          return state;
        },
      },
    );

    this.bindListeners();
  }

  get form() {
    return this.querySelector("form");
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get submit() {
    return this.querySelector('[type="submit"]');
  }

  get results() {
    return this.querySelector("[data-predictive-search-results]");
  }

  get limit() {
    return 5;
  }

  bindListeners() {
    this.form.addEventListener("submit", this.onFormSubmit);
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300),
    );
    this.input.addEventListener("focus", this.onFocus);
    this.addEventListener("focusout", this.onFocusOut);
    this.addEventListener("keyup", this.onKeyup);
    this.addEventListener("keydown", this.onKeydown);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onFormSubmit = (event) => {
    if (
      this.submit.getAttribute("aria-disabled") === "true" ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  };

  onChange = () => {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.state.open = false;
      this.state.loading = false;
      this.input.value = "";
      return;
    }
    this.getSearchResults(searchTerm);
  };

  onFocus = () => {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("has-results") === "true") {
      this.state.open = true;
    } else {
      this.getSearchResults(searchTerm);
    }
  };

  onFocusOut = () => {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.state.open = false;
        this.state.loading = false;
      }
    });
  };

  onKeyup = (event) => {
    if (!this.getQuery().length) {
      this.state.open = false;
      this.state.loading = false;
      this.input.value = "";
    }
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  };

  onKeydown = (event) => {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  };

  switchOption(direction) {
    if (!this.state.open || this.state.loading) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) selectedProduct.click();
  }

  processOpenChange() {
    this.input.setAttribute("aria-expanded", this.state.open);
    this.submit.setAttribute(
      "aria-disabled",
      !this.state.open && !this.input.value,
    );
    this.results.classList.toggle("is-open", this.state.open);

    if (!this.state.open) {
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute("aria-selected", false);
      this.input.setAttribute("aria-activedescendant", "");
    }
  }

  processLoadingChange() {
    this.loadingOverlay =
      this.loadingOverlay || this.querySelector(".c-loading-overlay");

    this.results.setAttribute("aria-busy", this.state.loading);
    this.results.classList.toggle("is-loading", this.state.loading);
    this.loadingOverlay.classList.toggle("hidden", !this.state.loading);

    if (this.state.loading) {
      this.statusElement =
        this.statusElement || this.querySelector(".c-predictive-search-status");
      this.setLiveRegionText(this.dataset.loadingText);
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.state.loading = true;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm,
      )}&${encodeURIComponent(
        "resources[type]",
      )}=product,collection,article,page&${encodeURIComponent(
        "resources[limit]",
      )}=${this.limit}&section_id=predictive-search-results`,
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(
            "#shopify-section-predictive-search-results",
          ).innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.state.open = false;
        this.state.loading = false;
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.results.innerHTML = resultsMarkup;
    this.setAttribute("has-results", true);

    this.setLiveRegionResults();
    this.state.open = true;
    this.state.loading = false;
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  setLiveRegionResults() {
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }
}
